import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { PrimaryButton, TextLink } from "@/components/fionova/Primitives";
import { useAuth } from "@/context/AuthContext";
import { getJourneyProgress, startJourney } from "@/services/unsaidService";
import { claimGuestJourney, journey } from "@/lib/journey";
import { onboardingState } from "@/lib/onboardingState";
import { LegalNotice } from "@/components/fionova/LegalNotice";

const STEPS = ["The Unsaid", "Emotional X-Ray", "Daily Battle Plan", "Future Self", "Purpose Engine"];

// Entry to the full guided Ellie journey (~10–15 minutes). An account is optional: guests start right away.
export default function Journey() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [guest, setGuest] = useState(null); // guest journey status in this browser
  const [ready, setReady] = useState(false);
  const first = (user?.name || "").split(" ")[0];

  useEffect(() => {
    if (loading) return;
    (async () => {
      if (user && journey.guestId()) { await claimGuestJourney(); await refresh(); }
      if (!user && journey.guestId()) {
        const p = await getJourneyProgress();
        if (!p.status) journey.setGuestId(null);
        setGuest(p.status || null);
      }
      setReady(true);
    })();
  }, [loading, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = async () => {
    setBusy(true); setError("");
    const res = await startJourney();
    if (res.data?.entitled) { navigate("/app"); return; }
    if (res.status === 429) { setBusy(false); setError("guest_limit"); return; }
    if (res.status === 403) { navigate("/journey/complete"); return; }
    if (!res.ok) { setBusy(false); setError("Something interrupted us. Please try again in a moment."); return; }
    const status = user ? user.journey_status : guest;
    if (res.data?.journey_id) journey.setGuestId(res.data.journey_id);
    let resume = null;
    if (status === "active") {
      const p = await getJourneyProgress();
      if (p.data && Object.keys(p.data).length) journey.restore(p.data);
      resume = p.step || journey.lastStep();
    } else journey.resetSteps();
    journey.on();
    if (user?.name) onboardingState.setAccount({ name: user.name, email: user.email });
    if (user) await refresh();
    navigate(resume || "/the-unsaid");
  };

  const status = user ? user.journey_status : guest;
  const finished = status === "completed";
  return (
    <OnboardingLayout testId="page-journey" contentClassName="justify-center py-12">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col items-center text-center">
        <EllieOrb size={104} testId="journey-ellie-avatar" />
        <p className="eyebrow mt-7 text-[11px] text-teal-700">Your guided journey with Ellie</p>
        <h1 className="mt-3 text-[32px] font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl" data-testid="journey-title">
          {first ? `Hi ${first}, I'm Ellie.` : "Hi, I'm Ellie."} <span className="fio-gradient-text">Let's take some time for you.</span>
        </h1>
        <p className="mt-5 max-w-md text-[15.5px] leading-relaxed text-slate-700">
          Over the next 10–15 minutes, I'd like to really get to know what you're carrying, what matters to you, and where you want to go. There are no right answers — just yours.
        </p>
      </motion.div>

      <ol className="fio-glass fio-hairline mx-auto mt-8 w-full max-w-md space-y-2 rounded-3xl p-5" data-testid="journey-steps">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-3 text-sm text-slate-700">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600/10 text-xs font-semibold text-teal-700">{i + 1}</span>{s}
          </li>
        ))}
        <li className="pt-1 text-xs text-slate-500">Then a personal reflection on everything we've explored together.</li>
      </ol>

      <div className="mx-auto mt-8 w-full max-w-md">
        {loading || !ready ? null : user?.entitled && !journey.guestId() && finished ? (
          <PrimaryButton data-testid="journey-go-app" onClick={() => navigate("/app")}>Continue with Ellie <ArrowRight className="h-4 w-4" /></PrimaryButton>
        ) : finished ? (
          <PrimaryButton data-testid="journey-see-reflection" onClick={() => navigate("/journey/complete")}>See my reflection <ArrowRight className="h-4 w-4" /></PrimaryButton>
        ) : (
          <>
            <PrimaryButton data-testid="journey-begin" onClick={begin} disabled={busy || error === "guest_limit"}>
              {busy ? "Getting ready…" : status === "active" ? "Continue my journey" : "Begin with Ellie"} <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
            <LegalNotice action="beginning" testId="journey-legal" className="mt-3" />
            {!user && (
              <>
                <p className="mt-4 flex items-start gap-2 rounded-2xl bg-white/70 px-4 py-3 text-left text-[13px] leading-relaxed text-slate-600 ring-1 ring-slate-200" data-testid="journey-guest-note">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <span>No account needed to begin. As a guest, your progress is kept privately in this browser and on our servers for 7 days. A free account saves it permanently (under our normal account retention policies), so you can return any time, on any device.</span>
                </p>
                <div className="mt-4 flex flex-col items-center gap-2 text-center">
                  <TextLink data-testid="journey-create-account" onClick={() => navigate("/begin?next=journey")}>Create a free account first</TextLink>
                  <TextLink data-testid="journey-login" onClick={() => navigate("/login?next=journey")}>I already have an account</TextLink>
                </div>
              </>
            )}
          </>
        )}
        {error === "guest_limit" ? (
          <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })} className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-center text-sm leading-relaxed text-amber-900 ring-1 ring-amber-200" data-testid="journey-guest-limit">
            Many people on this network have started a guest journey today. You can still begin right now with a free account. It's free and keeps your progress safe.
            <div className="mt-3"><PrimaryButton data-testid="journey-guest-limit-create" onClick={() => navigate("/begin?next=journey")}>Create my free account</PrimaryButton></div>
          </div>
        ) : error ? <p className="mt-3 text-center text-sm text-rose-600" data-testid="journey-error">{error}</p> : null}
      </div>
    </OnboardingLayout>
  );
}
